import EditPageContainer from '@app/components/EditPageContainer'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import NationalPlaceSelector from '@app/components/NationalPlaceSelector'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import * as React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Input } from 'reactstrap'
import { actionCreators, ICityEditState } from '../modules/cityEdit'
import { NationalPlace } from '@app/models/NationalPlace'

interface IConnectionViewRouterProps {
  citySlug: string
}

type IComponentProps = ICityEditState &
  ICurrentUserState &
  RouteComponentProps<IConnectionViewRouterProps> &
  typeof actionCreators

export default class CityEditPage extends React.PureComponent<IComponentProps> {
  public dropzoneStyle: React.CSSProperties
  public dropzoneActiveStyle: React.CSSProperties

  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)

    this.dropzoneStyle = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    this.dropzoneActiveStyle = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }
  }

  public componentDidMount() {
    const { citySlug } = this.props.match.params

    this.props.loadCity(citySlug)
  }

  public componentWillUnmount() {
    this.props.resetState()
  }

  public render() {
    if (!this.props.city) {
      return <div />
    }

    return (
      <EditPageContainer
        saveDisabled={false}
        title={
          this.props.match.params.citySlug
            ? this.props.city.place.cityState
            : 'New client'
        }
        onSaveClick={this._saveCity}
        onCancelClick={this._cancel}
      >
        <div className='container my-2'>
          {this._renderBasicInfo()}
          <h5 className='section-title'>Retail Node Maps</h5>
          {this._renderRetailNodeMaps()}
          <h5 className='section-title'>Regional Retail Maps</h5>
          {this._renderRegionalRetailMaps()}
          <h5 className='section-title'>Competitors in the Market Maps</h5>
          {this._renderCompetitorMaps()}
          <h5 className='section-title'>Traffic Count Maps</h5>
          {this._renderTrafficCountMaps()}
          <h5 className='section-title'>Top Brokers</h5>
          {this._renderTopBrokers()}
          <h5 className='section-title'>Chamber of Commerce Files</h5>
          {this._renderChamberOfCommerceFiles()}
          <h5 className='section-title'>Photos</h5>
          {this._renderPhotos()}
          <h5 className='section-title'>Attachments</h5>
          {this._renderAttachments()}
        </div>
      </EditPageContainer>
    )
  }

  private _saveCity() {
    this.props.save(this.props.city.id)
  }

  private _cancel() {
    this.props.history.goBack()
  }

  private _renderBasicInfo() {
    return (
      <div>
        {!this.props.city.id && (
          <div>
            <NationalPlaceSelector
              onChange={(e) => this.props.setPlace(e)}
              onInputChange={(e) =>
                this.props.setPlace(new NationalPlace({ cityState: e }))
              }
              required
              name='CityAndState'
              value={
                this.props.city.place ? this.props.city.place.cityState : ''
              }
            />
          </div>
        )}
        {this.props.city.id && (
          <h5 className='section-title'>{this.props.city.place.cityState}</h5>
        )}

        <Input
          className='mt-2'
          onChange={(e) => this.props.setOverview(e.target.value)}
          placeholder='Overview'
          type='textarea'
          style={{ minHeight: '8rem', resize: 'vertical' }}
          value={this.props.city.overview}
        />
      </div>
    )
  }

  private _renderAttachments() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-attachments'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select files for the city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-attachments'
          dataSource={this.props.city.attachments}
        />
      </div>
    )
  }

  private _renderPhotos() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-photos'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select photos for the city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-photos'
          dataSource={this.props.city.photos}
        />
      </div>
    )
  }

  private _renderChamberOfCommerceFiles() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-chamber-of-commerce'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select Chamber of Commerce files for
            the city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-chamber-of-commerce'
          dataSource={this.props.city.chamberOfCommerceFiles}
        />
      </div>
    )
  }

  private _renderTopBrokers() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-top-brokers'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select Top Broker files for the city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-top-brokers'
          dataSource={this.props.city.topBrokers}
        />
      </div>
    )
  }

  private _renderTrafficCountMaps() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-traffic-count-maps'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select Traffic Count Maps for the
            city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-traffic-count-maps'
          dataSource={this.props.city.trafficCountMaps}
        />
      </div>
    )
  }

  private _renderCompetitorMaps() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-competitor-maps'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select Competitor Maps for the city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-competitor-maps'
          dataSource={this.props.city.competitorsMaps}
        />
      </div>
    )
  }

  private _renderRegionalRetailMaps() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-regional-retail-maps'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select Regional Retail Maps for the
            city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-regional-retail-maps'
          dataSource={this.props.city.regionalMaps}
        />
      </div>
    )
  }

  private _renderRetailNodeMaps() {
    return (
      <div>
        <FileDropzone
          style={this.dropzoneStyle}
          identifier='city-retail-nodemaps'
          activeStyle={this.dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select Retail Node Maps for the city
          </p>
        </FileDropzone>
        <FileProgress
          identifier='city-retail-nodemaps'
          dataSource={this.props.city.retailNodeMaps}
        />
      </div>
    )
  }
}
